import { generatePath } from 'react-router-dom';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Response,
  BaseTransaction,
  Pagination,
  GeneralLedgerTransaction,
} from '@ui/src';
import { exchangeTransactionsListApi } from '../services';
import { statusesTransactionsApi } from '../../../../store/statuses/services';
import { transactionApi } from '../../../transactions/store/services';
import { StatusUpdate } from '../../../transactions/type';

import ROUTES from '../../../../routes';

export interface ExchangeListState {
  transactions?: BaseTransaction[];
  pagination?: Pagination;
  expandedTransactionId?: string;
  selectedTake?: number;
  collapseAllRows?: boolean;
  collapseAllExchangeWithoutCorrectionRows?: boolean;
  exchangeWithoutCorrections?: BaseTransaction[];
  exchangeWithoutCorrectionExpandedTransactionId?: string;
}

const initialState: ExchangeListState = {
  transactions: undefined,
};

export const exchangeListSlice = createSlice({
  name: 'exchangeList',
  initialState,
  reducers: {
    setSelectedTake: (state, action: PayloadAction<number>) => {
      state.selectedTake = action.payload;
    },
    setCollapseAllRows: (state, action: PayloadAction<boolean>) => {
      state.collapseAllRows = action.payload;
    },
    setExpandedTransactionId: (state, action: PayloadAction<string>) => {
      state.expandedTransactionId = action.payload;
    },
    setExchangeWithoutCorrectionCollapseAllRows: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.collapseAllRows = action.payload;
    },
    setExchangeWithoutCorrectionExpandedTransactionId: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.exchangeWithoutCorrectionExpandedTransactionId = action.payload;
    },
    clearExchangeListState: (state) => {
      state.transactions = undefined;
      state.pagination = undefined;
      state.expandedTransactionId = undefined;
      state.selectedTake = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      exchangeTransactionsListApi.endpoints.getExchangeList.matchFulfilled,
      (state, action: PayloadAction<Response<BaseTransaction>>) => {
        const nextTransactions = action.payload.data.edges.map((item) => ({
          ...item.node,
          deletedAt: item?.node?.deletedAt || '',
          url: generatePath(ROUTES.EXCHANGE_READ, {
            transactionId: item.node.id,
          }),
          marginCurrency:
            item.node.exchangeCurrencyFixedId ===
            item.node.exchangeCurrencyFromId
              ? item.node.exchangeCurrencyFrom?.name
              : item.node.exchangeCurrencyTo?.name,
          generalLedgerTransactions: {
            edges: [],
            pageInfo: undefined,
          },
          exchangeType:
            item.node.exchangeCurrencyFixedId ===
            item.node.exchangeCurrencyFromId
              ? 'Sell'
              : 'Buy',
          values: [],
        }));

        state.transactions = nextTransactions;
        state.pagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      exchangeTransactionsListApi.endpoints.getExchangeWithoutCorrection
        .matchFulfilled,
      (state, action: PayloadAction<{ data: BaseTransaction[] }>) => {
        const nextTransactions = action.payload.data.map((item) => ({
          ...item,
          deletedAt: item?.deletedAt || '',
          url: generatePath(ROUTES.EXCHANGE_WITHOUT_CORRECTIONS_ITEM, {
            transactionId: item.id,
          }),
          marginCurrency:
            item.exchangeCurrencyFixedId === item.exchangeCurrencyFromId
              ? item.exchangeCurrencyFrom?.name
              : item.exchangeCurrencyTo?.name,
          generalLedgerTransactions: {
            edges: [],
            pageInfo: undefined,
          },
          exchangeType:
            item.exchangeCurrencyFixedId === item.exchangeCurrencyFromId
              ? 'Sell'
              : 'Buy',
          values: [],
        }));

        state.exchangeWithoutCorrections = nextTransactions;
      },
    );
    builder.addMatcher(
      exchangeTransactionsListApi.endpoints.createExchangeTransaction
        .matchFulfilled,
      (state, action: PayloadAction<{ data: BaseTransaction }>) => {
        let nextTransactions: BaseTransaction[] = [];

        if (state.transactions?.length) {
          nextTransactions = ([] as BaseTransaction[]).concat(
            state.transactions,
          );
        }

        nextTransactions.unshift({
          ...action.payload.data,
          deletedAt: action.payload.data?.deletedAt || '',
          url: generatePath(ROUTES.EXCHANGE_READ, {
            transactionId: action.payload.data.id,
          }),
          marginCurrency:
            action.payload.data.exchangeCurrencyFixedId ===
            action.payload.data.exchangeCurrencyFromId
              ? action.payload.data.exchangeCurrencyFrom?.name
              : action.payload.data.exchangeCurrencyTo?.name,
          generalLedgerTransactions: {
            edges: [],
            pageInfo: undefined,
          },
          exchangeType:
            action.payload.data.exchangeCurrencyFixedId ===
            action.payload.data.exchangeCurrencyFromId
              ? 'Sell'
              : 'Buy',
          values: [],
        });

        state.transactions = nextTransactions;

        if (state.selectedTake) {
          state.selectedTake += 1;
        }
      },
    );
    builder.addMatcher(
      exchangeTransactionsListApi.endpoints.getExchangeListStatuses
        .matchFulfilled,
      (state, action: PayloadAction<Response<StatusUpdate>>) => {
        const { edges } = action.payload.data;
        const nextTransactions = state?.transactions?.map((item) => {
          const result = { ...item };

          const currentTransactionStatus = edges.find(
            (transaction) => transaction.node.id === result.id,
          );

          if (
            currentTransactionStatus &&
            currentTransactionStatus.node.baseellaStatus !==
              result.baseellaStatus
          ) {
            result.baseellaStatus =
              currentTransactionStatus.node.baseellaStatus;
          }

          return result;
        });

        state.transactions = nextTransactions;
      },
    );
    builder.addMatcher(
      exchangeTransactionsListApi.endpoints.updateExchangeTransaction
        .matchFulfilled,
      (state, action: PayloadAction<{ data: BaseTransaction }>) => {
        const nextTransactions = state.transactions?.map((item) => {
          let result = { ...item };

          if (result.id === action.payload.data.id) {
            result = {
              ...result,
              ...action.payload.data,
              deletedAt: action.payload.data?.deletedAt || '',
              url: generatePath(ROUTES.EXCHANGE_READ, {
                transactionId: action.payload.data.id,
              }),
            };
          }

          return result;
        });

        state.transactions = nextTransactions;
      },
    );
    builder.addMatcher(
      statusesTransactionsApi.endpoints.changeTransactionStatus.matchFulfilled,
      (state, action: PayloadAction<{ data: BaseTransaction }>) => {
        if (state.transactions) {
          const nextTransactions = state.transactions.map((item) => {
            const result = { ...item };

            if (result.id === action.payload.data.id) {
              result.baseellaStatus = action.payload.data.baseellaStatus;
            }

            return result;
          });

          state.transactions = nextTransactions;
        }
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getGeneralLedgerTransactionValues.matchFulfilled,
      (
        state,
        action: PayloadAction<Response<GeneralLedgerTransaction>> & {
          meta: {
            arg: {
              originalArgs: string;
            };
          };
        },
      ) => {
        if (state.transactions) {
          const nextGeneralLedgerTransactions = {
            edges: action.payload.data.edges.map((item) => ({
              cursor: item.cursor,
              node: {
                ...item.node,
                url: generatePath(ROUTES.EXCHANGE_GL, { glId: item.node.id }),
              },
            })),
            pageInfo: action.payload.data.pageInfo,
          };

          const nextTransactions = state.transactions.map((item) => {
            const result = { ...item };

            if (result.id === action.meta.arg.originalArgs) {
              result.generalLedgerTransactions = nextGeneralLedgerTransactions;
            }

            return result;
          });

          state.transactions = nextTransactions;
        }

        if (state.exchangeWithoutCorrections) {
          const nextGeneralLedgerTransactions = {
            edges: action.payload.data.edges.map((item) => ({
              cursor: item.cursor,
              node: {
                ...item.node,
                url: generatePath(ROUTES.EXCHANGE_GL, { glId: item.node.id }),
              },
            })),
            pageInfo: action.payload.data.pageInfo,
          };

          const nextTransactions = state.exchangeWithoutCorrections.map(
            (item) => {
              const result = { ...item };

              if (result.id === action.meta.arg.originalArgs) {
                result.generalLedgerTransactions =
                  nextGeneralLedgerTransactions;
              }

              return result;
            },
          );

          state.exchangeWithoutCorrections = nextTransactions;
        }
      },
    );
  },
});

export const {
  clearExchangeListState,
  setExpandedTransactionId,
  setSelectedTake,
  setCollapseAllRows,
  setExchangeWithoutCorrectionExpandedTransactionId,
  setExchangeWithoutCorrectionCollapseAllRows,
} = exchangeListSlice.actions;

export default exchangeListSlice.reducer;
